import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import * as chatAPI from '../../services/chat';
import { ChatUIComponent } from '@syncfusion/ej2-react-interactive-chat';
import {
    CHAT_TIMESTAMP_FORMAT,
    escapeHtml,
    htmlToPlainText,
    linkifyToHtml,
    formatUsDateTime
} from '../../utils/chatText';
import './Chat.css';

// Play notification sound - double beep
const playNotificationSound = () => {
    try {
        console.log('🔔 Playing notification sound');
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();

        // First beep
        const oscillator1 = audioContext.createOscillator();
        const gainNode1 = audioContext.createGain();

        oscillator1.connect(gainNode1);
        gainNode1.connect(audioContext.destination);

        oscillator1.frequency.value = 600;
        oscillator1.type = 'sine';

        gainNode1.gain.setValueAtTime(0.4, audioContext.currentTime);
        gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);

        oscillator1.start(audioContext.currentTime);
        oscillator1.stop(audioContext.currentTime + 0.15);

        // Second beep (slightly higher pitch)
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();

        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);

        oscillator2.frequency.value = 800;
        oscillator2.type = 'sine';

        gainNode2.gain.setValueAtTime(0.4, audioContext.currentTime + 0.2);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.35);

        oscillator2.start(audioContext.currentTime + 0.2);
        oscillator2.stop(audioContext.currentTime + 0.35);
    } catch (error) {
        console.error('Failed to play notification sound:', error);
    }
};

const TEAM_CHAT_STATUS_OPTIONS = [
    { value: 'online', label: 'Online' },
    { value: 'away', label: 'Away' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'offline', label: 'Offline' }
];

const TEAM_CHAT_STATUS_SETTER_ROLES = new Set(['Support', 'Recruiter', 'Interpreter']);

const isEventBroadcastChat = (chat) => chat?.metadata?.broadcastType === 'event';

const isServerMessageId = (id) => /^[a-f0-9]{24}$/i.test(String(id || ''));

// Sort order for the chat list: broadcasts and groups, then people who are logged in, then the rest
const LOGGED_IN_STATUSES = new Set(['online', 'away', 'meeting']);

const TeamChat = ({ onUnreadCountChange, isPanelOpen }) => {
    const { user } = useAuth();
    const { socket, connected: sharedSocketConnected } = useSocket();
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const isBroadcastReadOnly = isEventBroadcastChat(selectedChat) && user?.role !== 'GlobalSupport';
    const [messages, setMessages] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [showNewChat, setShowNewChat] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [onlineUsers, setOnlineUsers] = useState(new Set());
    const [userStatuses, setUserStatuses] = useState(new Map());
    const [myChatStatus, setMyChatStatus] = useState('online');
    const [chatStatusUpdating, setChatStatusUpdating] = useState(false);
    const [typingUsers, setTypingUsers] = useState(new Map());
    const [notification, setNotification] = useState(null);
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [showBroadcastComposer, setShowBroadcastComposer] = useState(false);
    const [broadcasting, setBroadcasting] = useState(false);
    const [showBroadcastManager, setShowBroadcastManager] = useState(false);
    const [deletingMessageId, setDeletingMessageId] = useState(null);
    const [removingBroadcast, setRemovingBroadcast] = useState(false);
    // Result of a delete/remove. Stays until dismissed or replaced (WCAG 2.2.1).
    const [actionMessage, setActionMessage] = useState(null);
    const typingTimeoutRef = useRef(new Map());
    const typingIndicatorTimeoutRef = useRef(null);

    const socketRef = useRef(null);
    const chatsRef = useRef([]);
    const chatRef = useRef(null);
    const selectedChatRef = useRef(null);
    const joinedChatsRef = useRef(new Set());
    const [socketConnected, setSocketConnected] = useState(false);
    const canSetTeamChatStatus = TEAM_CHAT_STATUS_SETTER_ROLES.has(user?.role);

    // Keep emit call sites working against the shared app socket
    useEffect(() => {
        socketRef.current = socket;
        setSocketConnected(!!sharedSocketConnected);
    }, [socket, sharedSocketConnected]);

    // Define handleTyping before effects that use it
    const handleTyping = useCallback((isTyping) => {
        if (selectedChat && socketRef.current) {
            socketRef.current.emit('typing', {
                chatId: selectedChat._id,
                isTyping
            });
        }
    }, [selectedChat]);

    // Keep a live ref of chats for use inside socket handlers
    useEffect(() => {
        chatsRef.current = chats;
    }, [chats]);

    // Attach chat listeners to the shared SocketContext connection (do not create/disconnect here)
    useEffect(() => {
        if (!socket || !user) {
            return;
        }

        const typingTimeouts = typingTimeoutRef.current;

        const handleConnect = () => {
            setSocketConnected(true);
            joinedChatsRef.current.clear();
            socket.emit('request-online-users');
            socket.emit('get-chat-status');
        };

        const handleDisconnect = () => {
            setSocketConnected(false);
            setChatStatusUpdating(false);
        };

        const handleError = () => {
            setChatStatusUpdating(false);
        };

        const handleNewMessage = (data) => {
            console.log('📩 New message received:', data);

            if (!user || !user._id) {
                console.warn('User not loaded, skipping message processing');
                return;
            }

            const currentChatId = selectedChatRef.current?._id;
            const isCurrentChat = data.chatId === currentChatId;
            const isOwnMessage = data.message.sender._id === user._id;

            if (isCurrentChat) {
                const newMsg = convertToSyncfusionMessage(data.message);
                setMessages(prev => [...prev, newMsg]);

                if (socketRef.current) {
                    socketRef.current.emit('mark-read', { chatId: data.chatId });
                }
            }

            if (!isCurrentChat && !isOwnMessage) {
                playNotificationSound();
                const senderName = data.message.sender.name;
                // Stays until dismissed or replaced by the next one (WCAG 2.2.1)
                setNotification({
                    chatId: data.chatId,
                    sender: senderName,
                    message: htmlToPlainText(data.message.content)
                });
            }

            setChats(prevChats => {
                const idx = prevChats.findIndex(chat => chat._id === data.chatId);

                if (idx !== -1) {
                    return prevChats.map(chat => {
                        if (chat._id !== data.chatId) return chat;
                        return {
                            ...chat,
                            lastMessage: data.message,
                            unreadCount: isCurrentChat
                                ? 0
                                : (isOwnMessage ? (chat.unreadCount || 0) : (chat.unreadCount || 0) + 1)
                        };
                    });
                }

                if (!isOwnMessage) {
                    const sender = data.message?.sender;
                    const minimalChat = {
                        _id: data.chatId,
                        type: 'direct',
                        participants: sender && user ? [
                            {
                                user: {
                                    _id: user._id,
                                    name: user.name,
                                    avatarUrl: user.avatarUrl || '',
                                    role: user.role,
                                    assignedBooth: user.assignedBooth || null
                                }
                            },
                            {
                                user: {
                                    _id: sender._id,
                                    name: sender.name,
                                    avatarUrl: sender.avatarUrl || '',
                                    role: sender.role,
                                    assignedBooth: sender.assignedBooth || null
                                }
                            }
                        ] : [],
                        lastMessage: data.message,
                        unreadCount: 1
                    };
                    return [minimalChat, ...prevChats];
                }

                return prevChats;
            });
        };

        const handleChatNotification = (data) => {
            console.log('📨 Chat notification received:', data);

            const currentChatId = selectedChatRef.current?._id;
            const isCurrentChat = data.chatId && data.chatId === currentChatId;
            const isOwnMessage = data.message?.sender?._id && user && data.message.sender._id === user._id;

            if (!isCurrentChat && !isOwnMessage) {
                try {
                    playNotificationSound();
                } catch (e) {
                    console.error('Failed to play notification sound from chat-notification:', e);
                }
            }

            const senderName = data.message?.sender?.name;
            const content = data.message?.content;
            if (senderName && content && !isCurrentChat && !isOwnMessage) {
                setNotification({
                    chatId: data.chatId,
                    sender: senderName,
                    message: htmlToPlainText(content)
                });
            }

            setChats(prevChats => {
                const idx = prevChats.findIndex(chat => chat._id === data.chatId);
                const sender = data.message?.sender;

                if (idx !== -1) {
                    return prevChats.map(chat => {
                        if (chat._id !== data.chatId) return chat;
                        return {
                            ...chat,
                            lastMessage: data.message,
                            unreadCount: Math.max(chat.unreadCount || 0, data.unreadCount || 1)
                        };
                    });
                }

                if (data.chat) {
                    const notifiedChat = {
                        ...data.chat,
                        unreadCount: data.unreadCount || 1
                    };
                    return [notifiedChat, ...prevChats];
                }

                if (sender && user) {
                    const minimalChat = {
                        _id: data.chatId,
                        type: 'direct',
                        participants: [
                            {
                                user: {
                                    _id: user._id,
                                    name: user.name,
                                    avatarUrl: user.avatarUrl || '',
                                    role: user.role,
                                    assignedBooth: user.assignedBooth || null
                                }
                            },
                            {
                                user: {
                                    _id: sender._id,
                                    name: sender.name,
                                    avatarUrl: sender.avatarUrl || '',
                                    role: sender.role,
                                    assignedBooth: sender.assignedBooth || null
                                }
                            }
                        ],
                        lastMessage: data.message,
                        unreadCount: data.unreadCount || 1
                    };
                    return [minimalChat, ...prevChats];
                }

                return prevChats;
            });
        };

        const handleOnlineUsersList = (data) => {
            setOnlineUsers(new Set(data.userIds));
            if (data.chatStatuses) {
                setUserStatuses(new Map(Object.entries(data.chatStatuses)));
            }
        };

        const handleUserOnline = (data) => {
            setOnlineUsers(prev => {
                const newSet = new Set(prev);
                newSet.add(data.userId);
                return newSet;
            });
            if (data.userId) {
                setUserStatuses(prev => {
                    const newMap = new Map(prev);
                    newMap.set(data.userId, data.chatStatus || 'online');
                    return newMap;
                });
            }
        };

        const handleUserOffline = (data) => {
            setOnlineUsers(prev => {
                const newSet = new Set(prev);
                newSet.delete(data.userId);
                return newSet;
            });
            if (data.userId) {
                setUserStatuses(prev => {
                    const newMap = new Map(prev);
                    newMap.set(data.userId, 'offline');
                    return newMap;
                });
            }
        };

        const handleUserChatStatusChanged = (data) => {
            if (!data?.userId) return;
            setUserStatuses(prev => {
                const newMap = new Map(prev);
                newMap.set(data.userId, data.status || 'offline');
                return newMap;
            });
        };

        const handleChatStatus = (data) => {
            if (!data?.userId) return;
            setUserStatuses(prev => {
                const newMap = new Map(prev);
                newMap.set(data.userId, data.status || 'online');
                return newMap;
            });
            if (data.userId === user?._id) {
                setMyChatStatus(data.status || 'online');
            }
        };

        const handleChatStatusUpdated = (data) => {
            if (!data?.userId) return;
            setUserStatuses(prev => {
                const newMap = new Map(prev);
                newMap.set(data.userId, data.status || 'online');
                return newMap;
            });
            if (data.userId === user?._id) {
                setMyChatStatus(data.status || 'online');
                setChatStatusUpdating(false);
            }
        };

        const handleUserTyping = (data) => {
            if (data.chatId !== selectedChatRef.current?._id) return;
            setTypingUsers(prev => {
                const newMap = new Map(prev);
                if (data.isTyping) {
                    newMap.set(data.userId, data.userName);

                    if (typingTimeoutRef.current.has(data.userId)) {
                        clearTimeout(typingTimeoutRef.current.get(data.userId));
                    }

                    const timeout = setTimeout(() => {
                        setTypingUsers(prevTyping => {
                            const next = new Map(prevTyping);
                            next.delete(data.userId);
                            return next;
                        });
                        typingTimeoutRef.current.delete(data.userId);
                    }, 3000);

                    typingTimeoutRef.current.set(data.userId, timeout);
                } else {
                    newMap.delete(data.userId);
                    if (typingTimeoutRef.current.has(data.userId)) {
                        clearTimeout(typingTimeoutRef.current.get(data.userId));
                        typingTimeoutRef.current.delete(data.userId);
                    }
                }
                return newMap;
            });
        };

        const handleMessageDeleted = (data) => {
            if (!data?.chatId || !data?.messageId) return;
            if (data.chatId === selectedChatRef.current?._id) {
                setMessages(prev => prev.filter(msg => msg.id !== data.messageId));
            }
            setChats(prevChats => prevChats.map(chat => (
                chat._id === data.chatId ? { ...chat, lastMessage: data.lastMessage || null } : chat
            )));
        };

        const handleChatRemoved = (data) => {
            if (!data?.chatId) return;
            setChats(prevChats => prevChats.filter(chat => chat._id !== data.chatId));
            joinedChatsRef.current.delete(data.chatId);
            setNotification(prev => (prev?.chatId === data.chatId ? null : prev));
            if (selectedChatRef.current?._id === data.chatId) {
                selectedChatRef.current = null;
                setSelectedChat(null);
                setMessages([]);
                setActionMessage({ type: 'info', text: 'This event broadcast was removed by Global Support.' });
            }
        };

        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('error', handleError);
        socket.on('new-message', handleNewMessage);
        socket.on('chat-notification', handleChatNotification);
        socket.on('online-users-list', handleOnlineUsersList);
        socket.on('user-online', handleUserOnline);
        socket.on('user-offline', handleUserOffline);
        socket.on('user-chat-status-changed', handleUserChatStatusChanged);
        socket.on('chat-status', handleChatStatus);
        socket.on('chat-status-updated', handleChatStatusUpdated);
        socket.on('user-typing', handleUserTyping);
        socket.on('message-deleted', handleMessageDeleted);
        socket.on('chat-removed', handleChatRemoved);

        if (socket.connected) {
            handleConnect();
        }

        return () => {
            typingTimeouts.forEach(timeout => clearTimeout(timeout));
            typingTimeouts.clear();

            socket.off('connect', handleConnect);
            socket.off('disconnect', handleDisconnect);
            socket.off('error', handleError);
            socket.off('new-message', handleNewMessage);
            socket.off('chat-notification', handleChatNotification);
            socket.off('online-users-list', handleOnlineUsersList);
            socket.off('user-online', handleUserOnline);
            socket.off('user-offline', handleUserOffline);
            socket.off('user-chat-status-changed', handleUserChatStatusChanged);
            socket.off('chat-status', handleChatStatus);
            socket.off('chat-status-updated', handleChatStatusUpdated);
            socket.off('user-typing', handleUserTyping);
            socket.off('message-deleted', handleMessageDeleted);
            socket.off('chat-removed', handleChatRemoved);
        };
        // convertToSyncfusionMessage is stable enough for event-time use from this render cycle
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket, user?._id]);

    useEffect(() => {
        loadChats();
        loadParticipants();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Join all chat rooms when chats are loaded and socket is connected
    useEffect(() => {
        if (!socketConnected || !socketRef.current || chats.length === 0) {
            return;
        }

        chats.forEach(chat => {
            if (!joinedChatsRef.current.has(chat._id)) {
                socketRef.current.emit('join-chat', { chatId: chat._id });
                joinedChatsRef.current.add(chat._id);
            }
        });
    }, [chats, socketConnected]);

    // Reset joined chat tracking when chat list clears (e.g., logout)
    useEffect(() => {
        if (chats.length === 0) {
            joinedChatsRef.current.clear();
        }
    }, [chats.length]);

    // Monitor messages changes
    useEffect(() => {
        console.log('💬 Messages state updated:', messages.length, 'messages');
        if (messages.length > 0) {
            console.log('💬 Latest message:', messages[messages.length - 1]);
        }
    }, [messages]);

    // Process author names to display booth name for recruiters when viewed by GlobalSupport
    useEffect(() => {
        if (user?.role !== 'GlobalSupport' || !selectedChat) return;

        // Wait for Syncfusion to render messages
        const timer = setTimeout(() => {
            const messageElements = document.querySelectorAll('.syncfusion-chat-wrapper .e-chat-ui .e-message');
            
            messageElements.forEach((messageEl, index) => {
                // Skip if already processed
                if (messageEl.dataset.boothProcessed === 'true') return;
                
                // Get the message data
                const message = messages[index];
                if (!message || !message.author?.boothName) return;

                // Only process bot messages (messages from others, not current user)
                const isBotMessage = messageEl.classList.contains('e-bot');
                if (!isBotMessage) return;

                // Find the author name element - try multiple selectors
                let authorEl = messageEl.querySelector('.e-message-author') ||
                              messageEl.querySelector('.e-author-name') ||
                              messageEl.querySelector('[class*="author"]') ||
                              messageEl.querySelector('.e-message-header') ||
                              messageEl.querySelector('.e-message-content')?.previousElementSibling;

                // If we can't find a specific author element, try to find text content that matches the name
                if (!authorEl) {
                    const allElements = messageEl.querySelectorAll('*');
                    for (let el of allElements) {
                        if (el.textContent && el.textContent.includes('|||BOOTH:')) {
                            authorEl = el;
                            break;
                        }
                    }
                }

                if (authorEl) {
                    // Check if we already added the booth name
                    if (authorEl.querySelector('.recruiter-booth-name')) {
                        messageEl.dataset.boothProcessed = 'true';
                        return;
                    }

                    // Process text content if it contains the booth separator
                    const textContent = authorEl.textContent || '';
                    if (textContent.includes('|||BOOTH:')) {
                        const parts = textContent.split('|||BOOTH:');
                        if (parts.length === 2) {
                            const namePart = parts[0].trim();
                            const boothPart = parts[1].split('|||')[0].trim();
                            
                            // Clear and rebuild the content
                            authorEl.innerHTML = '';
                            
                            // Add name
                            const nameSpan = document.createElement('span');
                            nameSpan.textContent = namePart;
                            authorEl.appendChild(nameSpan);
                            
                            // Add booth name
                            const boothSpan = document.createElement('div');
                            boothSpan.className = 'recruiter-booth-name';
                            boothSpan.textContent = boothPart;
                            authorEl.appendChild(boothSpan);
                        }
                    } else {
                        // If no separator found, just add the booth name after the existing content
                        const boothSpan = document.createElement('div');
                        boothSpan.className = 'recruiter-booth-name';
                        boothSpan.textContent = message.author.boothName;
                        authorEl.appendChild(boothSpan);
                    }
                    
                    messageEl.dataset.boothProcessed = 'true';
                }
            });
        }, 300);

        return () => clearTimeout(timer);
    }, [messages, selectedChat, user]);

    // Monitor online users changes
    useEffect(() => {
        console.log('👥 Online users state updated:', onlineUsers.size, 'users online');
        console.log('👥 Online user IDs:', Array.from(onlineUsers));
    }, [onlineUsers]);

    // Clear selected chat when panel closes
    useEffect(() => {
        if (isPanelOpen === false) {
            console.log('🔇 Chat panel closed - clearing selected chat ref for sound alerts');
            selectedChatRef.current = null;
            // On mobile, also clear selected chat to show list when reopening
            if (window.innerWidth <= 768) {
                setSelectedChat(null);
            }
        } else if (isPanelOpen === true && selectedChat) {
            console.log('🔊 Chat panel opened - restoring selected chat ref:', selectedChat._id);
            selectedChatRef.current = selectedChat;
        }
    }, [isPanelOpen, selectedChat]);

    // Typing detection via keyboard events
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (selectedChat && document.activeElement?.closest('.e-chat-ui')) {
                // User is typing in the chat input
                handleTyping(true);

                // Clear existing timeout
                if (typingIndicatorTimeoutRef.current) {
                    clearTimeout(typingIndicatorTimeoutRef.current);
                }

                // Set new timeout to stop typing indicator
                typingIndicatorTimeoutRef.current = setTimeout(() => {
                    handleTyping(false);
                }, 1000);
            }
        };

        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
            if (typingIndicatorTimeoutRef.current) {
                clearTimeout(typingIndicatorTimeoutRef.current);
            }
        };
    }, [selectedChat, handleTyping]);

    const convertToSyncfusionMessage = (msg) => {
        // For GlobalSupport users viewing recruiters, include booth name in author name
        const shouldShowBooth = user?.role === 'GlobalSupport' && 
                                 msg.sender?.role === 'Recruiter' && 
                                 msg.sender?.assignedBooth;
        
        const boothName = shouldShowBooth && msg.sender.assignedBooth?.name 
            ? msg.sender.assignedBooth.name 
            : null;
        
        // Syncfusion renders the author and the text as HTML, so both are escaped here.
        // Include booth name in author name with a special separator for CSS targeting
        const authorName = boothName
            ? `${escapeHtml(msg.sender.name)}|||BOOTH:${escapeHtml(boothName)}|||`
            : escapeHtml(msg.sender.name);

        return {
            id: msg._id,
            // Plain text with clickable links; older messages were saved as editor HTML
            text: linkifyToHtml(htmlToPlainText(msg.content)),
            plainText: htmlToPlainText(msg.content),
            author: {
                id: msg.sender._id,
                user: authorName,
                avatarUrl: msg.sender.avatarUrl || '',
                role: msg.sender.role,
                boothName: boothName
            },
            timeStamp: new Date(msg.createdAt),
            timeStampFormat: CHAT_TIMESTAMP_FORMAT
        };
    };

    const loadChats = async () => {
        try {
            setLoading(true);
            const data = await chatAPI.getChats();
            // Initialize unread counts if not present
            const chatsWithUnreadCount = data.map(chat => ({
                ...chat,
                unreadCount: chat.unreadCount || 0
            }));
            setChats(chatsWithUnreadCount);
        } catch (error) {
            console.error('Error loading chats:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadParticipants = async () => {
        try {
            const data = await chatAPI.getParticipants();
            // Debug: Log participants to check assignedBooth
            if (user?.role === 'GlobalSupport') {
                console.log('📋 Participants loaded:', data.map(p => ({
                    name: p.name,
                    role: p.role,
                    assignedBooth: p.assignedBooth,
                    hasBooth: !!p.assignedBooth
                })));
            }
            setParticipants(data);
        } catch (error) {
            console.error('Error loading participants:', error);
        }
    };

    const loadMessages = useCallback(async (chatId) => {
        try {
            const data = await chatAPI.getMessages(chatId);
            const syncfusionMessages = data.map(convertToSyncfusionMessage);
            setMessages(syncfusionMessages);

            if (socketRef.current) {
                socketRef.current.emit('mark-read', { chatId });
                // No need to join here - already joined all chats on mount
            }

            setChats(prevChats => prevChats.map(chat =>
                chat._id === chatId ? { ...chat, unreadCount: 0 } : chat
            ));
        } catch (error) {
            console.error('Error loading messages:', error);
        }
    }, []);

    const handleSelectChat = (chat) => {
        setSelectedChat(chat);
        selectedChatRef.current = chat;
        setShowNewChat(false);
        setShowBroadcastManager(false);
        setActionMessage(null);
        setNotification(prev => (prev?.chatId === chat._id ? null : prev));
        loadMessages(chat._id);
    };

    const handleBackToList = () => {
        setSelectedChat(null);
        selectedChatRef.current = null;
    };

    const handleSendMessage = (args) => {
        const messageText = args?.message?.text || args?.text || '';

        console.log('📤 Sending message:', messageText);
        console.log('📤 Selected chat:', selectedChat?._id);

        if (!selectedChat || !messageText.trim()) {
            console.warn('⚠️ Cannot send message - no chat selected or empty message');
            return;
        }

        if (isEventBroadcastChat(selectedChat) && user?.role !== 'GlobalSupport') {
            console.warn('⚠️ Event Broadcast is read-only for non-GlobalSupport users');
            return;
        }

        // The Syncfusion input gives HTML (&amp;, <br>); store plain text
        const content = htmlToPlainText(messageText).trim();
        if (!content) {
            return;
        }

        try {
            if (socketRef.current) {
                console.log('📤 Emitting send-message via socket');
                // Stop typing indicator
                socketRef.current.emit('typing', {
                    chatId: selectedChat._id,
                    isTyping: false
                });

                socketRef.current.emit('send-message', {
                    chatId: selectedChat._id,
                    content,
                    type: 'text'
                });
                console.log('✅ Message sent to server');
            } else {
                console.error('❌ Socket not connected');
            }
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const handleStartNewChat = async (participant) => {
        try {
            const chat = await chatAPI.createDirectChat(participant._id);
            setChats(prev => {
                const filtered = prev.filter(existingChat => existingChat._id !== chat._id);
                return [chat, ...filtered];
            });
            setSelectedChat(chat);
            setShowNewChat(false);

            // Join the new chat room immediately
            if (socketRef.current) {
                socketRef.current.emit('join-chat', { chatId: chat._id });
            }

            selectedChatRef.current = chat;
            loadMessages(chat._id);
        } catch (error) {
            console.error('Error creating chat:', error);
        }
    };

    const handleBroadcastMessage = async () => {
        if (user?.role !== 'GlobalSupport') return;
        const content = broadcastMessage.trim();
        if (!content) return;

        try {
            setBroadcasting(true);
            const response = await chatAPI.broadcastEventMessage(content);
            const broadcastChat = response?.chat;
            if (broadcastChat) {
                setChats(prev => {
                    const filtered = prev.filter(existingChat => existingChat._id !== broadcastChat._id);
                    return [{ ...broadcastChat, unreadCount: 0 }, ...filtered];
                });
                setSelectedChat(broadcastChat);
                selectedChatRef.current = broadcastChat;

                if (socketRef.current) {
                    socketRef.current.emit('join-chat', { chatId: broadcastChat._id });
                    joinedChatsRef.current.add(broadcastChat._id);
                }

                await loadMessages(broadcastChat._id);
            }
            setBroadcastMessage('');
            setShowBroadcastComposer(false);
        } catch (error) {
            console.error('Error broadcasting message:', error);
        } finally {
            setBroadcasting(false);
        }
    };

    const canManageSelectedBroadcast = user?.role === 'GlobalSupport' && isEventBroadcastChat(selectedChat);

    const deleteMessage = useCallback(async (messageId) => {
        const chatId = selectedChatRef.current?._id;
        if (!chatId || !isServerMessageId(messageId)) return;
        if (!window.confirm('Delete this message for everyone in this chat? This cannot be undone.')) {
            return;
        }

        setDeletingMessageId(messageId);
        try {
            const result = await chatAPI.deleteChatMessage(chatId, messageId);
            setMessages(prev => prev.filter(msg => msg.id !== messageId));
            setChats(prevChats => prevChats.map(chat => (
                chat._id === chatId ? { ...chat, lastMessage: result?.lastMessage || null } : chat
            )));
            setActionMessage({ type: 'success', text: 'Message deleted.' });
        } catch (error) {
            setActionMessage({
                type: 'error',
                text: error?.message || 'The message could not be deleted. Please try again.'
            });
        } finally {
            setDeletingMessageId(null);
        }
    }, []);

    // Syncfusion's message toolbar Delete only removes the message from this screen.
    // Make it a real delete, and explain when the message is not the user's to delete.
    const handleMessageToolbarClick = useCallback((args) => {
        if (!String(args?.item?.prefixIcon || '').includes('e-chat-trash')) return;
        args.cancel = true;
        const message = args.message;
        const isOwn = message?.author?.id === user?._id;
        const isManagedBroadcast = user?.role === 'GlobalSupport' && isEventBroadcastChat(selectedChatRef.current);
        if (!isOwn && !isManagedBroadcast) {
            setActionMessage({ type: 'error', text: 'You can only delete your own messages.' });
            return;
        }
        deleteMessage(message?.id);
    }, [user?._id, user?.role, deleteMessage]);

    const messageToolbarSettings = React.useMemo(() => ({
        itemClicked: handleMessageToolbarClick
    }), [handleMessageToolbarClick]);

    const handleRemoveBroadcast = async () => {
        const chat = selectedChatRef.current;
        if (!chat || !canManageSelectedBroadcast) return;
        const eventName = chat.event?.name ? ` for ${chat.event.name}` : '';
        if (!window.confirm(`Remove this event broadcast${eventName} from everyone's chat list? This cannot be undone.`)) {
            return;
        }

        setRemovingBroadcast(true);
        try {
            await chatAPI.removeChat(chat._id);
            setChats(prev => prev.filter(existingChat => existingChat._id !== chat._id));
            joinedChatsRef.current.delete(chat._id);
            selectedChatRef.current = null;
            setSelectedChat(null);
            setMessages([]);
            setShowBroadcastManager(false);
            setActionMessage({ type: 'success', text: 'Event broadcast removed.' });
        } catch (error) {
            setActionMessage({
                type: 'error',
                text: error?.message || 'The broadcast could not be removed. Please try again.'
            });
        } finally {
            setRemovingBroadcast(false);
        }
    };

    const getChatTitle = (chat) => {
        if (chat.type === 'direct') {
            const otherParticipant = chat.participants.find(
                p => p.user._id !== user?._id
            );
            return otherParticipant?.user?.name || 'Unknown';
        }
        return chat.name;
    };

    const getChatRole = (chat) => {
        if (chat.type === 'direct') {
            const otherParticipant = chat.participants.find(
                p => p.user._id !== user?._id
            );
            return otherParticipant?.user?.role || '';
        }
        return '';
    };

    const getChatAvatar = (chat) => {
        if (chat.type === 'direct') {
            const otherParticipant = chat.participants.find(
                p => p.user._id !== user?._id
            );
            return otherParticipant?.user?.avatarUrl || null;
        }
        return chat.booth?.logoUrl || null;
    };

    const getBoothDisplayName = (assignedBooth) => {
        if (!assignedBooth) return '';
        if (typeof assignedBooth === 'string') return assignedBooth;
        return assignedBooth.name || assignedBooth.company || '';
    };

    const getChatBoothName = (chat) => {
        if (chat.type !== 'direct') return '';
        const otherParticipant = chat.participants.find(
            p => p.user._id !== user?._id
        );
        return getBoothDisplayName(otherParticipant?.user?.assignedBooth);
    };

    const getOtherParticipantId = (chat) => {
        if (chat.type === 'direct') {
            const otherParticipant = chat.participants.find(
                p => p.user._id !== user?._id
            );
            return otherParticipant?.user?._id;
        }
        return null;
    };

    const getEffectiveChatStatus = useCallback((targetUserId) => {
        if (!targetUserId) return 'offline';
        const explicitStatus = userStatuses.get(targetUserId);
        if (explicitStatus) return explicitStatus;
        return onlineUsers.has(targetUserId) ? 'online' : 'offline';
    }, [onlineUsers, userStatuses]);

    const handleSetMyChatStatus = useCallback((newStatus) => {
        if (!socketRef.current || !canSetTeamChatStatus) return;
        setChatStatusUpdating(true);
        socketRef.current.emit('set-chat-status', { status: newStatus });
    }, [canSetTeamChatStatus]);

    // The server already drops direct chats the Team Chat permission rules no longer allow
    const filteredChats = chats.filter(chat =>
        getChatTitle(chat).toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Logged-in people first (Sprint 7). Array.sort is stable, so within each group
    // the server's order (latest activity first) is kept.
    const chatSortGroup = (chat) => {
        if (chat.type !== 'direct') return 0;
        return LOGGED_IN_STATUSES.has(getEffectiveChatStatus(getOtherParticipantId(chat))) ? 1 : 2;
    };
    const sortedChats = [...filteredChats].sort((a, b) => chatSortGroup(a) - chatSortGroup(b));
    const loggedInChatCount = sortedChats.filter(chat => chatSortGroup(chat) === 1).length;
    const notLoggedInChatCount = sortedChats.filter(chat => chatSortGroup(chat) === 2).length;

    // Filter participants: exclude current user and users already in chat list
    const existingChatUserIds = new Set(
        chats
            .filter(chat => chat.type === 'direct')
            .map(chat => getOtherParticipantId(chat))
            .filter(Boolean)
    );

    // Filter participants: exclude current user and users already in chat list,
    // then sort so online users appear at the top
    const filteredParticipants = participants
        .filter(p => {
            // Exclude current user
            if (p._id === user?._id) return false;
            
            // Exclude users already in chat list
            if (existingChatUserIds.has(p._id)) return false;
            
            // Search filter
            if (!p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
            
            // Who appears here is decided on the server by the Team Chat permission rules
            // (SuperAdmin > Team Chat Permissions), so there is no role filtering here.
            return true;
        })
        .sort((a, b) => {
            const aOnline = onlineUsers.has(a._id);
            const bOnline = onlineUsers.has(b._id);
            if (aOnline === bOnline) {
                return a.name.localeCompare(b.name);
            }
            return aOnline ? -1 : 1;
        });

    const currentUser = user ? {
        id: user._id,
        user: user.name,
        avatarUrl: user.avatarUrl || ''
    } : null;

    // Calculate total unread count
    const totalUnreadCount = chats.reduce((total, chat) => total + (chat.unreadCount || 0), 0);

    // Notify parent of unread count changes
    useEffect(() => {
        console.log('📊 Total unread count:', totalUnreadCount);
        if (onUnreadCountChange) {
            onUnreadCountChange(totalUnreadCount);
        }
    }, [totalUnreadCount, onUnreadCountChange]);

    if (loading || !user) {
        return (
            <div className="team-chat-container">
                <div className="team-chat-loading">
                    {loading ? 'Loading chats...' : 'Loading user...'}
                </div>
            </div>
        );
    }

    return (
        <div className={`team-chat-container ${selectedChat ? 'chat-open' : ''}`} data-unread-count={totalUnreadCount}>
            {/* Always in the DOM so screen readers announce each new message */}
            <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                {notification ? `New message from ${notification.sender}: ${notification.message}` : ''}
            </div>

            {/* Notification toast: stays until dismissed or replaced (WCAG 2.2.1) */}
            {notification && (
                <div className="team-chat-notification-toast">
                    <div className="notification-icon" aria-hidden="true">💬</div>
                    <div className="notification-content">
                        <div className="notification-title">{notification.sender}</div>
                        <div className="notification-message">{notification.message}</div>
                    </div>
                    <button
                        type="button"
                        className="notification-close"
                        onClick={() => setNotification(null)}
                        aria-label={`Dismiss notification from ${notification.sender}`}
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
            )}

            {/* Sidebar - Chat List */}
            <div className="team-chat-sidebar">
                <div className="team-chat-sidebar-header">
                    <h2>Chats</h2>
                    <div className="team-chat-header-actions">
                        {user?.role === 'GlobalSupport' && (
                            <button
                                className={`team-chat-new-btn team-chat-broadcast-btn ${showBroadcastComposer ? 'active' : ''}`}
                                onClick={() => setShowBroadcastComposer(!showBroadcastComposer)}
                                aria-label="Broadcast message to all event users"
                                title="Broadcast message to all event users"
                            >
                                📣
                            </button>
                        )}
                        <button
                            type="button"
                            className="team-chat-new-btn"
                            onClick={() => setShowNewChat(!showNewChat)}
                            aria-label={showNewChat ? 'Cancel new chat' : 'Start new chat'}
                            aria-expanded={showNewChat}
                        >
                            <span aria-hidden="true">{showNewChat ? '✕' : '+'}</span>
                        </button>
                    </div>
                </div>

                <div className="team-chat-search">
                    {user?.role === 'GlobalSupport' && showBroadcastComposer && (
                        <div className="team-chat-broadcast-composer">
                            <label htmlFor="team-chat-broadcast-message">Broadcast to event users</label>
                            <textarea
                                id="team-chat-broadcast-message"
                                value={broadcastMessage}
                                onChange={(e) => setBroadcastMessage(e.target.value)}
                                rows={3}
                                maxLength={5000}
                                placeholder="Type a message to broadcast to everyone in this event..."
                            />
                            <button
                                type="button"
                                className="team-chat-broadcast-send"
                                onClick={handleBroadcastMessage}
                                disabled={broadcasting || !broadcastMessage.trim()}
                            >
                                {broadcasting ? 'Sending...' : 'Send Broadcast'}
                            </button>
                        </div>
                    )}
                    {canSetTeamChatStatus && (
                        <div className="team-chat-status-controls team-chat-status-controls-sidebar">
                            <label htmlFor="team-chat-status-select">Your status</label>
                            <select
                                id="team-chat-status-select"
                                value={myChatStatus}
                                onChange={(e) => handleSetMyChatStatus(e.target.value)}
                                disabled={chatStatusUpdating}
                                aria-label="Set your team chat status"
                            >
                                {TEAM_CHAT_STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        aria-label="Search chats"
                    />
                </div>

                <div className="team-chat-action-region" role="status" aria-live="polite">
                    {actionMessage && actionMessage.type !== 'error' && (
                        <div className={`team-chat-action-message ${actionMessage.type}`}>
                            <span>{actionMessage.text}</span>
                            <button
                                type="button"
                                className="team-chat-action-dismiss"
                                onClick={() => setActionMessage(null)}
                                aria-label="Dismiss message"
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                    )}
                </div>
                <div className="team-chat-action-region" role="alert">
                    {actionMessage?.type === 'error' && (
                        <div className="team-chat-action-message error">
                            <span>{actionMessage.text}</span>
                            <button
                                type="button"
                                className="team-chat-action-dismiss"
                                onClick={() => setActionMessage(null)}
                                aria-label="Dismiss error"
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                    )}
                </div>

                <div className="team-chat-list">
                    {showNewChat ? (
                        <>
                            <div className="team-chat-list-header">New Chat</div>
                            {filteredParticipants.length === 0 ? (
                                <div className="team-chat-empty-state">
                                    No available users to chat with.
                                </div>
                            ) : (
                                filteredParticipants.map(participant => {
                                    const participantStatus = getEffectiveChatStatus(participant._id);
                                    return (
                                        <div
                                            key={participant._id}
                                            className="team-chat-list-item"
                                            onClick={() => handleStartNewChat(participant)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleStartNewChat(participant);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <div className="team-chat-avatar">
                                                {participant.avatarUrl ? (
                                                    <img src={participant.avatarUrl} alt="" />
                                                ) : (
                                                    <div className="team-chat-avatar-placeholder">
                                                        {participant.name.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <span className={`team-chat-status-indicator ${participantStatus}`} />
                                            </div>
                                            <div className="team-chat-info">
                                                <div className="team-chat-name">
                                                    {participant.name} ({participant.role})
                                                </div>
                                                {getBoothDisplayName(participant.assignedBooth) && (
                                                    <div className="team-chat-booth-name">
                                                        {getBoothDisplayName(participant.assignedBooth)}
                                                    </div>
                                                )}
                                                <div className={`team-chat-status ${participantStatus}`}>
                                                    {TEAM_CHAT_STATUS_OPTIONS.find(option => option.value === participantStatus)?.label || 'Offline'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </>
                    ) : (
                        <>
                            {sortedChats.length === 0 ? (
                                <div className="team-chat-empty-state">
                                    No chats yet. Click + to start a new conversation.
                                </div>
                            ) : (
                                sortedChats.map((chat, index) => {
                                    const otherUserId = getOtherParticipantId(chat);
                                    const chatStatus = getEffectiveChatStatus(otherUserId);
                                    const role = getChatRole(chat);
                                    const group = chatSortGroup(chat);
                                    const startsGroup = group !== 0 && (index === 0 || chatSortGroup(sortedChats[index - 1]) !== group);

                                    return (
                                        <React.Fragment key={chat._id}>
                                        {startsGroup && (
                                            <h3 className="team-chat-list-header">
                                                {group === 1
                                                    ? `Logged in (${loggedInChatCount})`
                                                    : `Not logged in (${notLoggedInChatCount})`}
                                            </h3>
                                        )}
                                        <div
                                            className={`team-chat-list-item ${selectedChat?._id === chat._id ? 'active' : ''}`}
                                            onClick={() => handleSelectChat(chat)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleSelectChat(chat);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <div className="team-chat-avatar">
                                                {getChatAvatar(chat) ? (
                                                    <img src={getChatAvatar(chat)} alt="" />
                                                ) : (
                                                    <div className="team-chat-avatar-placeholder">
                                                        {getChatTitle(chat).charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                {chat.type === 'direct' && (
                                                    <span className={`team-chat-status-indicator ${chatStatus}`} />
                                                )}
                                            </div>
                                            <div className="team-chat-info">
                                                <div className="team-chat-header-row">
                                                    <div className="team-chat-name">
                                                        {getChatTitle(chat)} {role && `(${role})`}
                                                    </div>
                                                    {chat.unreadCount > 0 && (
                                                        <span className="team-chat-unread-badge">{chat.unreadCount}</span>
                                                    )}
                                                </div>
                                                {getChatBoothName(chat) && (
                                                    <div className="team-chat-booth-name">
                                                        {getChatBoothName(chat)}
                                                    </div>
                                                )}
                                                {chat.type === 'direct' && (
                                                    <div className={`team-chat-status ${chatStatus}`}>
                                                        {TEAM_CHAT_STATUS_OPTIONS.find(option => option.value === chatStatus)?.label || 'Offline'}
                                                    </div>
                                                )}
                                                {chat.lastMessage?.content && (
                                                    <div className="team-chat-last-message">
                                                        {htmlToPlainText(chat.lastMessage.content)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Main Chat Area with Syncfusion */}
            <div className="team-chat-main">
                {selectedChat ? (
                    <div className="syncfusion-chat-wrapper">
                        <div className="team-chat-header">
                            <button
                                className="team-chat-mobile-back"
                                onClick={handleBackToList}
                                aria-label="Back to chat list"
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="team-chat-header-info">
                                <div className="team-chat-avatar">
                                    {getChatAvatar(selectedChat) ? (
                                        <img src={getChatAvatar(selectedChat)} alt="" />
                                    ) : (
                                        <div className="team-chat-avatar-placeholder">
                                            {getChatTitle(selectedChat).charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div className="team-chat-title">
                                        {getChatTitle(selectedChat)} {getChatRole(selectedChat) && `(${getChatRole(selectedChat)})`}
                                    </div>
                                    <div className="team-chat-subtitle">
                                        {selectedChat.type === 'direct' ? (
                                            (() => {
                                                const otherUserId = getOtherParticipantId(selectedChat);
                                                const chatStatus = getEffectiveChatStatus(otherUserId);

                                                // Check if user is typing
                                                if (typingUsers.size > 0) {
                                                    const typingNames = Array.from(typingUsers.values());
                                                    return `${typingNames.join(', ')} ${typingNames.length === 1 ? 'is' : 'are'} typing...`;
                                                }

                                                return TEAM_CHAT_STATUS_OPTIONS.find(option => option.value === chatStatus)?.label || 'Offline';
                                            })()
                                        ) : isEventBroadcastChat(selectedChat)
                                            ? (isBroadcastReadOnly ? 'Broadcast · Read only' : 'Event Broadcast')
                                            : 'Group Chat'}
                                    </div>
                                </div>
                            </div>
                            {canManageSelectedBroadcast && (
                                <div className="team-chat-broadcast-actions">
                                    <button
                                        type="button"
                                        className="team-chat-broadcast-action"
                                        onClick={() => setShowBroadcastManager(prev => !prev)}
                                        aria-expanded={showBroadcastManager}
                                        aria-controls="team-chat-broadcast-manager"
                                    >
                                        {showBroadcastManager ? 'Done' : 'Manage messages'}
                                    </button>
                                    <button
                                        type="button"
                                        className="team-chat-broadcast-action danger"
                                        onClick={handleRemoveBroadcast}
                                        disabled={removingBroadcast}
                                    >
                                        {removingBroadcast ? 'Removing…' : 'Remove broadcast'}
                                    </button>
                                </div>
                            )}
                        </div>
                        {canManageSelectedBroadcast && showBroadcastManager && (
                            <section
                                id="team-chat-broadcast-manager"
                                className="team-chat-broadcast-manager"
                                aria-labelledby="team-chat-broadcast-manager-title"
                            >
                                <h3 id="team-chat-broadcast-manager-title">Broadcast messages</h3>
                                {messages.length === 0 ? (
                                    <p>There are no messages in this broadcast.</p>
                                ) : (
                                    <ul>
                                        {messages.map(msg => (
                                            <li key={msg.id}>
                                                <div className="team-chat-broadcast-manager-text">
                                                    <span className="team-chat-broadcast-manager-meta">
                                                        {formatUsDateTime(msg.timeStamp)}
                                                    </span>
                                                    <span>{msg.plainText}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="team-chat-broadcast-action danger"
                                                    onClick={() => deleteMessage(msg.id)}
                                                    disabled={deletingMessageId === msg.id}
                                                    aria-label={`Delete message sent ${formatUsDateTime(msg.timeStamp)}: ${msg.plainText.slice(0, 80)}`}
                                                >
                                                    {deletingMessageId === msg.id ? 'Deleting…' : 'Delete'}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        )}
                        {isBroadcastReadOnly && (
                            <div className="team-chat-readonly-banner" role="status">
                                Only Global Support can post in Event Broadcast. You can view messages here.
                            </div>
                        )}
                        {typingUsers.size > 0 && (
                            <div className="team-chat-typing-indicator">
                                <span className="typing-dot"></span>
                                <span className="typing-dot"></span>
                                <span className="typing-dot"></span>
                            </div>
                        )}
                        <ChatUIComponent
                            key={selectedChat._id}
                            ref={chatRef}
                            user={currentUser}
                            messages={messages}
                            messageSend={handleSendMessage}
                            timeStampFormat={CHAT_TIMESTAMP_FORMAT}
                            messageToolbarSettings={messageToolbarSettings}
                            placeholder={isBroadcastReadOnly ? 'This chat is read-only' : 'Type your message...'}
                            showHeader={false}
                            showFooter={!isBroadcastReadOnly}
                        />
                    </div>
                ) : (
                    <div className="team-chat-no-selection">
                        <h3>Select a chat to start messaging</h3>
                        <p>Choose a conversation from the sidebar or start a new one</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamChat;
